import {getNaturalFloatingBoxSize} from './getNaturalFloatingBoxSize'
import type {NaturalFloatingSize} from './getNaturalFloatingBoxSize'
import {
  expandCollapsedFloatingCloneHeights,
  relaxFloatingCloneSizeConstraints,
} from './relaxFloatingCloneSizeConstraints'

export type {NaturalFloatingSize}

/**
 * Clones a floating element into an unconstrained, invisible container and
 * returns the size it would have without maxWidth / maxHeight clamps.
 *
 * WebKit skips layout for `visibility: hidden` + off-screen (`left: -99999px`)
 * nodes, and treats percentage heights of a `max-content` parent as 0. Keep
 * the clone in-viewport (`opacity: 0`), drop max-height clamps, then only
 * rewrite descendants that actually collapsed vs the live tree.
 *
 * @see https://floating-ui.com/docs/size#using-with-flip
 */
export function measureNaturalFloatingSize(
  element: HTMLElement,
  keepContentMaxHeight = false,
): NaturalFloatingSize {
  const clone = element.cloneNode(true) as HTMLElement
  clone.setAttribute('aria-hidden', 'true')
  clone.style.position = 'fixed'
  clone.style.left = '0'
  clone.style.top = '0'
  clone.style.right = 'auto'
  clone.style.bottom = 'auto'
  clone.style.opacity = '0'
  clone.style.pointerEvents = 'none'
  clone.style.zIndex = '-1'
  clone.style.transform = 'none'
  relaxFloatingCloneSizeConstraints(clone, keepContentMaxHeight)

  document.body.appendChild(clone)
  void clone.offsetHeight
  expandCollapsedFloatingCloneHeights(clone, element)
  void clone.offsetHeight
  const size = getNaturalFloatingBoxSize(clone.getBoundingClientRect(), clone)
  clone.remove()
  return size
}
